import React, { useCallback, useEffect, useState } from 'react';
import { Plus, Send, Sparkles, FlaskConical } from 'lucide-react';
import { toast } from 'react-toastify';
import { redes, errorMsg } from './redes.api';
import Cuentas from './Cuentas';
import Crear from './Crear';
import Calendario from './Calendario';

/* Marketing › Redes sociales: el cliente conecta SU Instagram, Facebook (página) y LinkedIn
   y programa publicaciones con el material de la obra. La vidriera de plantillas queda en su pestaña. */

export default function RedesSociales({ user, customerInfo, vidriera, pestanaInicial, isAdmin }) {
  const ctx = {
    customerId: user?.customerId,
    feedId: user?.feedID,
    obra: user?.Name || user?.obraName || 'la obra',
    empresa: customerInfo?.name || '',
  };
  const [pestana, setPestana] = useState(pestanaInicial || 'publicar');
  const [estado, setEstado] = useState(null);
  const [lista, setLista] = useState(null);
  const [crear, setCrear] = useState(null); // null | {} | publicación a editar | { archivos, texto } de la vidriera
  // La vidriera se monta recién la primera vez que se abre (carga muchas fotos) y después queda viva.
  const [vidrieraVista, setVidrieraVista] = useState(pestanaInicial === 'plantillas');
  useEffect(() => { if (pestana === 'plantillas') setVidrieraVista(true); }, [pestana]);

  const cargar = useCallback(() => {
    if (!ctx.customerId) return;
    redes.estado(ctx.customerId).then(setEstado).catch((e) => { if (isAdmin) toast.error(errorMsg(e)); setEstado({ redes: [], modo: 'real', error: true }); });
    redes.publicaciones(ctx.customerId).then(setLista).catch(() => setLista([]));
  }, [ctx.customerId, isAdmin]);
  useEffect(() => { cargar(); }, [cargar]);

  // Mientras haya algo por salir, refresca solo (el ejecutor publica cada minuto).
  useEffect(() => {
    const pendiente = (lista || []).some((p) => p.estado === 'publicando' || (p.estado === 'programada' && Date.parse(p.fechaAt) - Date.now() < 10 * 60_000));
    if (!pendiente) return undefined;
    const t = setInterval(() => redes.publicaciones(ctx.customerId).then(setLista).catch(() => {}), 8000);
    return () => clearInterval(t);
  }, [lista, ctx.customerId]);

  // Vuelta de la pantalla de la red (?redes=ok|error|cancelado&red=...&msg=...)
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const r = q.get('redes');
    if (!r) return;
    const nombre = { instagram: 'Instagram', facebook: 'Facebook', linkedin: 'LinkedIn' }[q.get('red')] || 'La red';
    if (r === 'ok') toast.success(`${nombre} conectado${Number(q.get('n')) > 1 ? ` (${q.get('n')} cuentas)` : ''}. Ya podés programar.`);
    else if (r === 'cancelado') toast.info(q.get('msg') || 'No se conectó.');
    else toast.error(q.get('msg') || `No pudimos conectar ${nombre}.`);
    ['redes', 'red', 'n', 'msg'].forEach((k) => q.delete(k));
    window.history.replaceState(null, '', window.location.pathname + (q.toString() ? `?${q}` : '') + window.location.hash);
    setPestana('publicar');
  }, []);

  const conectadas = (estado?.redes || []).reduce((a, r) => a + r.cuentas.length, 0);
  const programadas = (lista || []).filter((p) => p.estado === 'programada').length;
  const publicadas = (lista || []).filter((p) => p.estado === 'publicada' || p.estado === 'parcial').length;

  const abrirCrear = (inicial = {}) => {
    if (!conectadas) { toast.info('Primero conectá al menos una red'); return; }
    setCrear(inicial);
  };
  const desdeVidriera = (pieza) => {
    setPestana('publicar');
    if (!conectadas) { toast.info('Conectá una red para programar la pieza'); return; }
    setCrear(pieza);
  };

  // Cliente sin ninguna red aprobada (o módulo sin publicar): sólo las plantillas, como antes.
  const admin = !!(isAdmin || estado?.admin);
  if (!admin && estado && !(estado.redes || []).length) return vidriera ? vidriera(undefined) : null;
  if (!admin && !estado) return <div className="h-40 bg-white border border-gray-200 rounded-2xl animate-pulse" />;

  return (
    <div className="space-y-4">
      <div className="bg-white border border-gray-200 rounded-2xl p-4 md:p-5 flex flex-col md:flex-row md:items-center gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="text-xl md:text-2xl font-bold text-gray-800">Redes sociales</h2>
            {admin && <span className="text-[11px] font-semibold bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">Vista admin</span>}
            {estado?.modo === 'simulado' && <span className="inline-flex items-center gap-1 text-[11px] font-semibold bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full"><FlaskConical size={12} />Modo prueba</span>}
          </div>
          <p className="text-sm text-gray-500 mt-0.5">Conectá tus redes y programá publicaciones con las fotos y el timelapse de tu obra. Salen solas, el día y la hora que elijas.</p>
        </div>
        <div className="flex bg-gray-100 rounded-xl p-1 self-start md:self-auto">
          {[['publicar', 'Mis redes', Send], ['plantillas', 'Plantillas', Sparkles]].map(([id, t, I]) => (
            <button key={id} type="button" onClick={() => setPestana(id)} className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-semibold ${pestana === id ? 'bg-white shadow text-gray-900' : 'text-gray-600'}`}><I size={15} />{t}</button>
          ))}
        </div>
      </div>

      {vidrieraVista && <div className={pestana === 'plantillas' ? '' : 'hidden'}>{vidriera ? vidriera(desdeVidriera) : null}</div>}

      {pestana === 'publicar' && (
        <>
          <div>
            <div className="text-sm font-semibold text-gray-700 mb-2">Tus redes</div>
            <Cuentas estado={estado} customerId={ctx.customerId} onCambio={cargar} />
          </div>

          <div className="grid grid-cols-3 gap-2 sm:gap-3">
            {[['Redes conectadas', conectadas], ['Programadas', programadas], ['Publicadas', publicadas]].map(([t, v]) => (
              <div key={t} className="bg-white border border-gray-200 rounded-2xl p-3 sm:p-4"><div className="text-[11px] sm:text-xs text-gray-500">{t}</div><div className="text-xl sm:text-2xl font-bold text-gray-900 mt-0.5">{estado && lista ? v : '…'}</div></div>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row gap-2">
            <button type="button" onClick={() => abrirCrear({})} className="inline-flex items-center justify-center gap-2 rounded-xl px-5 py-3 text-sm font-semibold bg-blue-600 hover:bg-blue-700 text-white"><Plus size={18} />Crear publicación</button>
            <button type="button" onClick={() => setPestana('plantillas')} className="inline-flex items-center justify-center gap-2 rounded-xl px-5 py-3 text-sm font-semibold border border-gray-300 text-gray-800 bg-white hover:bg-gray-50"><Sparkles size={16} />Usar una plantilla</button>
          </div>

          <Calendario lista={lista} onCambio={cargar} onEditar={(p) => setCrear(p)} onCrearEn={(fecha) => abrirCrear({ fecha })} />
        </>
      )}

      {crear && (
        <Crear
          ctx={ctx}
          estado={estado}
          inicial={crear}
          onClose={() => setCrear(null)}
          onGuardada={() => { setCrear(null); cargar(); }}
        />
      )}
    </div>
  );
}
